import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/modules/auth/stores/authStore';
import { api } from '@/services/api';
import { getLoginUrl } from '@/modules/auth/services/authService';
import { AdBanner, AD_SLOTS } from '@/modules/ads';

interface UserStats {
  level: number;
  tier: string;
  tierEmoji: string;
  title: string;
  totalExp: number;
  currentExp: number;
  expToNext: number;
  debaterCount: number;
  juryCount: number;
  wins: number;
  losses: number;
  draws: number;
  winRate: number;
  currentWinStreak: number;
  maxWinStreak: number;
  juryPriority: number;
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, isGuest, isAuthenticated, logout } = useAuthStore();
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // 게스트는 기본 통계만 표시
    if (isGuest || !isAuthenticated) {
      setStats({
        level: 1,
        tier: '브론즈',
        tierEmoji: '🥉',
        title: '게스트',
        totalExp: 0,
        currentExp: 0,
        expToNext: 100,
        debaterCount: 0,
        juryCount: 0,
        wins: 0,
        losses: 0,
        draws: 0,
        winRate: 0,
        currentWinStreak: 0,
        maxWinStreak: 0,
        juryPriority: 0,
      });
      setLoading(false);
      return;
    }

    const fetchStats = async () => {
      try {
        setLoading(true);
        const data = await api.get<UserStats>('/users/me/stats');
        setStats(data);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
        setError('통계를 불러오는데 실패했습니다');
        // 기본값 설정
        setStats({
          level: 1,
          tier: '브론즈',
          tierEmoji: '🥉',
          title: '신입 변론가',
          totalExp: 0,
          currentExp: 0,
          expToNext: 100,
          debaterCount: 0,
          juryCount: 0,
          wins: 0,
          losses: 0,
          draws: 0,
          winRate: 0,
          currentWinStreak: 0,
          maxWinStreak: 0,
          juryPriority: 0,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [isGuest, isAuthenticated]);

  const handleLogout = () => {
    if (isGuest) {
      logout();
      return;
    }
    if (confirm('로그아웃 하시겠습니까?')) {
      logout();
    }
  };

  const handleLogin = () => {
    window.location.href = getLoginUrl();
  };

  const totalDebates = stats ? stats.wins + stats.losses + stats.draws : 0;

  return (
    <div className="p-4 max-w-lg mx-auto">
      <h1 className="text-2xl font-bold mb-6">프로필</h1>

      {/* Profile Card */}
      <div className="card mb-6">
        <div className="flex items-center gap-4">
          {/* Avatar */}
          <div className="w-20 h-20 rounded-full bg-primary-500/20 border-2 border-primary-500 flex items-center justify-center overflow-hidden">
            {user?.avatarUrl ? (
              <img 
                src={user.avatarUrl} 
                alt="프로필" 
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-3xl">😎</span>
            )}
          </div>

          {/* Info */}
          <div className="flex-1">
            <h2 className="text-xl font-semibold">{user?.nickname}</h2>
            <p className="text-sm text-slate-400">{user?.email}</p>
            {loading ? (
              <div className="flex items-center gap-2 mt-2">
                <span className="px-2 py-1 bg-slate-700 rounded text-sm animate-pulse w-16 h-6"></span>
              </div>
            ) : stats && (
              <div className="flex items-center gap-2 mt-2">
                <span className="px-2 py-1 bg-primary-500/20 text-primary-300 rounded text-sm font-medium">
                  {stats.tierEmoji} Lv.{stats.level}
                </span>
                <span className="text-sm text-slate-400">{stats.title}</span>
              </div>
            )}
          </div>
        </div>

        {/* EXP Bar */}
        {!loading && stats && (
          <div className="mt-4">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-400">경험치</span>
              <span className="text-primary-400">
                {stats.currentExp} / {stats.expToNext}
              </span>
            </div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded-full transition-all"
                style={{
                  width: `${(stats.currentExp / stats.expToNext) * 100}%`,
                }}
              />
            </div>
          </div>
        )}

        {/* Tier Info */}
        {!loading && stats && (
          <div className="mt-4 p-3 bg-slate-700/50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-slate-400">티어</span>
              <span className="font-semibold">{stats.tierEmoji} {stats.tier}</span>
            </div>
          </div>
        )}
      </div>

      {/* Activity Stats */}
      <div className="card mb-6">
        <h3 className="font-semibold mb-4">활동 현황</h3>
        
        {loading ? (
          <div className="grid grid-cols-2 gap-4">
            {[1, 2].map(i => (
              <div key={i} className="text-center p-3 bg-slate-700/50 rounded-lg animate-pulse">
                <div className="h-8 w-12 mx-auto bg-slate-600 rounded mb-1"></div>
                <div className="h-4 w-16 mx-auto bg-slate-600 rounded"></div>
              </div>
            ))}
          </div>
        ) : stats && (
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-slate-700/50 rounded-lg">
              <p className="text-2xl font-bold text-primary-400">{stats.debaterCount}</p>
              <p className="text-xs text-slate-400">토론 참여</p>
            </div>
            <div className="text-center p-3 bg-slate-700/50 rounded-lg">
              <p className="text-2xl font-bold text-yellow-400">{stats.juryCount}</p>
              <p className="text-xs text-slate-400">배심원 참여</p>
            </div>
          </div>
        )}

        {/* Jury Priority */}
        {!loading && stats && stats.juryPriority > 0 && (
          <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <div className="flex items-center gap-2">
              <span className="text-yellow-400">⭐</span>
              <span className="text-sm text-yellow-300">
                배심원 우선권 {stats.juryPriority}점 - 다음 토론자 선발 확률 증가!
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="card mb-6">
        <h3 className="font-semibold mb-4">토론 전적</h3>
        
        {loading ? (
          <div className="grid grid-cols-3 gap-4 mb-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="text-center p-3 bg-slate-700/50 rounded-lg animate-pulse">
                <div className="h-8 w-10 mx-auto bg-slate-600 rounded mb-1"></div>
                <div className="h-4 w-12 mx-auto bg-slate-600 rounded"></div>
              </div>
            ))}
          </div>
        ) : stats && (
          <>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 bg-slate-700/50 rounded-lg">
                <p className="text-2xl font-bold text-pro">{stats.wins}</p>
                <p className="text-xs text-slate-400">승리</p>
              </div>
              <div className="text-center p-3 bg-slate-700/50 rounded-lg">
                <p className="text-2xl font-bold text-con">{stats.losses}</p>
                <p className="text-xs text-slate-400">패배</p>
              </div>
              <div className="text-center p-3 bg-slate-700/50 rounded-lg">
                <p className="text-2xl font-bold text-slate-300">{stats.draws}</p>
                <p className="text-xs text-slate-400">무승부</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-slate-700/50 rounded-lg">
                <p className="text-sm text-slate-400">승률</p>
                <p className="text-xl font-semibold">
                  {totalDebates > 0 ? (stats.winRate * 100).toFixed(1) : 0}%
                </p>
              </div>
              <div className="p-3 bg-slate-700/50 rounded-lg">
                <p className="text-sm text-slate-400">총 토론</p>
                <p className="text-xl font-semibold">{totalDebates}회</p>
              </div>
              <div className="p-3 bg-slate-700/50 rounded-lg">
                <p className="text-sm text-slate-400">현재 연승</p>
                <p className="text-xl font-semibold text-primary-400">
                  {stats.currentWinStreak > 0 && '🔥 '}{stats.currentWinStreak}
                </p>
              </div>
              <div className="p-3 bg-slate-700/50 rounded-lg">
                <p className="text-sm text-slate-400">최고 연승</p>
                <p className="text-xl font-semibold">{stats.maxWinStreak}</p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Settings */}
      <div className="card">
        <h3 className="font-semibold mb-4">설정</h3>
        
        <div className="space-y-2">
          {/* 게스트 로그인 버튼 */}
          {isGuest && (
            <>
              <button
                onClick={handleLogin}
                className="w-full p-3 text-left bg-primary-500/20 text-primary-300 hover:bg-primary-500/30 rounded-lg transition-colors font-semibold"
              >
                🔐 로그인하고 토론 참여하기
              </button>
              <hr className="border-slate-700 my-2" />
            </>
          )}
          
          {!isGuest && (
            <>
              <button 
                onClick={() => navigate('/profile/edit')}
                className="w-full p-3 text-left hover:bg-slate-700/50 rounded-lg transition-colors"
              >
                프로필 편집
              </button>
              <button 
                onClick={() => navigate('/profile/notifications')}
                className="w-full p-3 text-left hover:bg-slate-700/50 rounded-lg transition-colors"
              >
                알림 설정
              </button>
            </>
          )}
          <button 
            onClick={() => navigate('/terms')}
            className="w-full p-3 text-left hover:bg-slate-700/50 rounded-lg transition-colors"
          >
            이용약관
          </button>
          <button 
            onClick={() => navigate('/privacy')}
            className="w-full p-3 text-left hover:bg-slate-700/50 rounded-lg transition-colors"
          >
            개인정보 처리방침
          </button>
          <hr className="border-slate-700 my-2" />
          <button
            onClick={handleLogout}
            className="w-full p-3 text-left text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
          >
            {isGuest ? '게스트 나가기' : '로그아웃'}
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <p className="text-center text-yellow-500 text-sm mt-4">
          ⚠️ {error}
        </p>
      )}

      {/* 하단 광고 배너 */}
      <div className="mt-6 pb-4">
        <AdBanner 
          adSlot={AD_SLOTS.LOBBY_BANNER} 
          adFormat="horizontal"
        />
      </div>

      {/* Version */}
      <p className="text-center text-slate-500 text-sm mt-4">
        버전 0.1.0
      </p>
    </div>
  );
}
